import { create } from 'zustand';

import {
  saveStarPrintersToDB,
  getStarPrintersFromDB,
  saveDefaultStarPrinterToDB,
  getDefaultStarPrinterFromDB,
} from 'src/utils/Services/DB_Utils';

import { starPrintService } from 'src/services/printers';
import type { SavedPrinter, StarPrinterStatus } from 'src/services/printers';

import { resolvePrinterName } from './printer-name';
import { PrinterStoreType, PrinterLastError, PrinterReadyStatus } from './types';

function captureError(error: unknown, context: string): PrinterLastError {
  const message = error instanceof Error ? error.message : String(error);
  return { message, timestamp: Date.now(), context };
}

/** Map the new plugin's status shape onto the store's status fields. */
function mapStarStatus(status: StarPrinterStatus): {
  coverOpen: boolean;
  cutterError: boolean;
  offline: boolean;
  overTemp: boolean;
  receiptPaperEmpty: boolean;
} {
  return {
    coverOpen: status.coverOpen,
    cutterError: status.cutterError ?? false,
    offline: !status.online,
    overTemp: false,
    receiptPaperEmpty: status.paperEmpty,
  };
}

/** Whether a plugin status means the printer is ready to print. */
function isStatusReady(status: StarPrinterStatus): boolean {
  return status.online && !status.coverOpen && !status.paperEmpty && !status.cutterError;
}

export const usePrinterStore = create<PrinterStoreType>((set, get) => ({
  isPrinterReady: false,
  currentlyPrinting: false,
  status: null,
  lastError: null,
  helperOpen: false,
  helperMode: 'status',
  pendingRetry: null,

  // ── v4 printing flow: the single active printer + remembered list ───────────
  activePrinter: null,
  rememberedPrinters: [],

  loadPrinters: async () => {
    try {
      let state = await getStarPrintersFromDB();
      if (!state) {
        // First boot on the new schema: migrate from the legacy single
        // `defaultStarPrinter` key, if one was saved by an earlier build.
        const legacy = await getDefaultStarPrinterFromDB();
        state = legacy
          ? { activeId: legacy.identifier, printers: [legacy] }
          : { activeId: null, printers: [] };
        if (legacy) await saveStarPrintersToDB(state);
      }
      const active = state.printers.find((p) => p.identifier === state!.activeId) ?? null;
      set({ rememberedPrinters: state.printers, activePrinter: active });
    } catch (error) {
      console.error('[PrinterStore] loadPrinters failed:', error);
    }
  },

  setActivePrinter: async (printer: SavedPrinter) => {
    const remembered = get().rememberedPrinters;
    // Give newly-saved printers a short local name (`Printer #N - TYPE`); an
    // already-remembered printer keeps the name it was saved with.
    const named: SavedPrinter = { ...printer, name: resolvePrinterName(printer, remembered) };
    // Move the chosen printer to the front of the remembered list (dedupe by id).
    const others = remembered.filter((p) => p.identifier !== named.identifier);
    const printers = [named, ...others];
    set({ activePrinter: named, rememberedPrinters: printers });
    try {
      await saveStarPrintersToDB({ activeId: named.identifier, printers });
      // Mirror to the legacy key so starPrintService reconnects headlessly.
      await saveDefaultStarPrinterToDB(named);
    } catch (error) {
      console.error('[PrinterStore] setActivePrinter persist failed:', error);
    }
  },

  removeRememberedPrinter: async (identifier: string) => {
    const printers = get().rememberedPrinters.filter((p) => p.identifier !== identifier);
    const wasActive = get().activePrinter?.identifier === identifier;
    const activePrinter = wasActive ? null : get().activePrinter;
    set({ rememberedPrinters: printers, activePrinter });
    try {
      await saveStarPrintersToDB({ activeId: activePrinter?.identifier ?? null, printers });
      if (wasActive) await starPrintService.clearDefaultPrinter();
    } catch (error) {
      console.error('[PrinterStore] removeRememberedPrinter persist failed:', error);
    }
  },

  clearActivePrinter: async () => {
    const printers = get().rememberedPrinters;
    set({ activePrinter: null, isPrinterReady: false });
    try {
      await saveStarPrintersToDB({ activeId: null, printers });
      await starPrintService.clearDefaultPrinter();
    } catch (error) {
      console.error('[PrinterStore] clearActivePrinter persist failed:', error);
    }
  },

  applyStatus: (status: StarPrinterStatus) => {
    set((state) => ({
      ...state,
      isPrinterReady: isStatusReady(status),
      status: mapStarStatus(status),
    }));
  },

  markPrinterUnreachable: () => {
    set((state) => ({
      ...state,
      isPrinterReady: false,
      status: {
        coverOpen: false,
        cutterError: false,
        overTemp: false,
        receiptPaperEmpty: false,
        ...(state.status ?? {}),
        offline: true,
      },
    }));
  },

  clearLastError: () => {
    set({ lastError: null });
  },

  openHelper: (mode, retryFn) => {
    set({ helperOpen: true, helperMode: mode, pendingRetry: retryFn ?? null });
  },
  closeHelper: () => {
    set({ helperOpen: false, pendingRetry: null });
  },
  // Legacy aliases — every troubleshoot trigger lands in the unified helper.
  openTroubleshoot: (retryFn) => {
    get().openHelper('troubleshoot', retryFn);
  },
  closeTroubleshoot: () => {
    get().closeHelper();
  },

  setIsPrinterReady: (isReady: boolean) => {
    set((state) => ({
      ...state,
      isPrinterReady: isReady,
    }));
  },
  setPrinterIsReadyWithStatus: (status: PrinterReadyStatus) => {
    set((state) => ({
      ...state,
      isPrinterReady: true,
      status: {
        ...state.status,
        coverOpen: status.coverOpen,
        cutterError: status.cutterError,
        offline: status.offline,
        overTemp: status?.overTemp ?? false,
        receiptPaperEmpty: status.receiptPaperEmpty,
      },
    }));
  },

  reconnectPrinter: async () => {
    console.log('[PrinterStore] Manual printer reconnection triggered');
    try {
      const status = await starPrintService.getPrinterStatus({ forceReconnect: true });
      const ready = isStatusReady(status);
      set((state) => ({
        ...state,
        isPrinterReady: ready,
        lastError: ready ? null : state.lastError,
        status: mapStarStatus(status),
      }));
    } catch (error) {
      console.error('[PrinterStore] Reconnect failed:', error);
      // Mark unreachable (not just not-ready) so the auto-reconnect loop's
      // offline clock starts even when no drop event was ever observed.
      get().markPrinterUnreachable();
      set({ lastError: captureError(error, 'reconnect') });
    }
  },

  resetConnection: async (onStep) => {
    console.log('[PrinterStore] Full connection reset triggered');
    try {
      onStep?.('Reconnecting to printer...');
      const status = await starPrintService.getPrinterStatus({ forceReconnect: true });
      const ready = isStatusReady(status);
      onStep?.('Verifying printer status...');
      set((state) => ({
        ...state,
        isPrinterReady: ready,
        lastError: ready ? null : state.lastError,
        status: mapStarStatus(status),
      }));
    } catch (error) {
      console.error('[PrinterStore] Reset connection failed:', error);
      get().markPrinterUnreachable();
      set({ lastError: captureError(error, 'reset-connection') });
      throw error;
    }
  },
}));
